import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { SystemFiles } from '@app/core/interfaces/portal/system-files';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { ServiceProviderContextService } from '@app/shared/services/context/service-provider-context.service';
import { SystemContextService } from '@app/shared/services/context/system-context.service';
import { Cached } from '@app/utils/cached';
import { notNullish } from '@app/utils/rxjs';
import { PhoneBookModel } from '@business/phone-book/models/phone-book-model';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ContextHelperService {
  private _acdQueues: Cached<Queue[]> | null = null;

  get acdQueues(): Cached<Queue[]> {
    this._acdQueues ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        if (this.authenticationService.userIsEndUser()) {
          return this.reloadAndGet$(this.endUserContextService.acdAgents).pipe(
            map((agents) => agents.map((agent) => agent.queue))
          );
        } else {
          return this.reloadAndGet$(this.enterpriseContextService.acdQueues);
        }
      },
      id: ([enterpriseId]) => `context-helper:enterprise:${enterpriseId}:acd-queues`,
      rememberTtl: 10_000,
      routeParameters: ['enterpriseId'],
    });

    return this._acdQueues;
  }

  private _callCenterId: Cached<number | null> | null = null;

  get callCenterId(): Cached<number | null> {
    this._callCenterId ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        if (this.authenticationService.userIsEndUser()) {
          return this.reloadAndGet$(this.endUserContextService.callCenter).pipe(
            map((context) => context?.callCenterId ?? null)
          );
        } else {
          return this.reloadAndGet$(this.enterpriseContextService.callCenter).pipe(
            map((callCenter) => callCenter?.id ?? null)
          );
        }
      },
      id: ([enterpriseId]) => `context-helper:enterprise:${enterpriseId}:call-center-queues`,
      rememberTtl: 10_000,
      routeParameters: ['enterpriseId'],
    });

    return this._callCenterId;
  }

  private _callCenter: Cached<Queue[]> | null = null;

  get callCenterQueues(): Cached<Queue[]> {
    this._callCenter ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        if (this.authenticationService.userIsEndUser()) {
          return this.reloadAndGet$(this.endUserContextService.callCenter).pipe(
            map((callCenter) => callCenter?.agents.map((agent) => agent.queue) ?? [])
          );
        } else {
          return this.reloadAndGet$(this.enterpriseContextService.callCenter).pipe(
            map((callCenter) => callCenter?.queues ?? [])
          );
        }
      },
      id: ([enterpriseId]) => `context-helper:enterprise:${enterpriseId}:call-center-queues`,
      rememberTtl: 10_000,
      routeParameters: ['enterpriseId'],
    });

    return this._callCenter;
  }

  private _phonebook: Cached<PhoneBookModel[]> | null = null;

  get phoneBookContacts(): Cached<PhoneBookModel[]> {
    this._phonebook ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.authenticationService.userIsEndUser()
          ? this.reloadAndGet$(this.endUserContextService.phoneBook)
          : this.reloadAndGet$(this.enterpriseContextService.phoneBook);
      },
      id: ([enterpriseId]) => `context-helper:enterprise:${enterpriseId}:phonebook`,
      rememberTtl: 10_000,
      routeParameters: ['enterpriseId'],
    });

    return this._phonebook;
  }

  private _systemFiles: Cached<SystemFiles> | null = null;

  get systemFiles(): Cached<SystemFiles> {
    this._systemFiles ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([serviceProviderId]) => {
        return serviceProviderId == null
          ? this.reloadAndGet$(this.systemContextService.files)
          : this.reloadAndGet$(this.serviceProviderContextService.files);
      },
      id: ([serviceProviderId]) =>
        serviceProviderId != null
          ? `context-helper:service-provider:${serviceProviderId}:files`
          : 'context-helper:system:files',
      requireAllRouteParameters: false,
      routeParameters: ['serviceProviderId'],
    });

    return this._systemFiles;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private endUserContextService: EndUserContextService,
    private enterpriseContextService: EnterpriseContextService,
    private serviceProviderContextService: ServiceProviderContextService,
    private systemContextService: SystemContextService
  ) {}

  /**
   * Reloads the given cached instance and waits for the next value.
   * If the cached instance throws an error then it will propagate it.
   */
  reloadAndGet$<T>(cached: Cached<T>): Observable<T> {
    cached.reload();

    return new Observable<T>((subscriber) => {
      const dataSubscription = cached.data$.pipe(first()).subscribe((data) => {
        subscriber.next(data);
        subscriber.complete();
      });

      const errorSubscription = cached.error$.pipe(notNullish(), first()).subscribe((error) => {
        subscriber.error(error);
        subscriber.complete();
      });

      return () => {
        dataSubscription.unsubscribe();
        errorSubscription.unsubscribe();
      };
    });
  }
}
